"use client";

import { useEffect, useRef } from "react";

const therapies = [
  {
    id: "massagem-terapeutica",
    name: "Massagem Terapêutica",
    icon: "💆‍♀️",
    duration: "60 min",
    description: "Técnica manual profunda que alivia tensões musculares crônicas, melhora a circulação sanguínea e linfática, reduz inflamações e promove recuperação muscular acelerada.",
    benefits: ["Alívio de tensões musculares", "Melhora da circulação", "Redução de dores crônicas", "Recuperação muscular"],
    ideal: "Dores musculares, pós-treino, tensão por estresse",
  },
  {
    id: "massagem-relaxante",
    name: "Massagem Relaxante",
    icon: "🌸",
    duration: "60 min",
    description: "Movimentos suaves e rítmicos que induzem estado profundo de relaxamento, reduzem o cortisol, melhoram a qualidade do sono e restauram o equilíbrio emocional.",
    benefits: ["Redução do estresse", "Melhora do sono", "Equilíbrio emocional", "Bem-estar profundo"],
    ideal: "Estresse, ansiedade, insônia, cansaço mental",
  },
  {
    id: "massagem-estetica",
    name: "Massagem Estética",
    icon: "✨",
    duration: "60 min",
    description: "Tratamento focado na modelagem corporal, redução de medidas e melhora da aparência da pele. Combina drenagem linfática com técnicas de estimulação do tecido adiposo.",
    benefits: ["Modelagem corporal", "Redução de medidas", "Drenagem linfática", "Firmeza da pele"],
    ideal: "Celulite, retenção de líquidos, modelagem corporal",
  },
  {
    id: "ventosaterapia",
    name: "Ventosaterapia",
    icon: "🌀",
    duration: "45 min",
    description: "Técnica milenar chinesa que utiliza ventosas para criar pressão negativa sobre a pele, estimulando a circulação profunda, aliviando dores e promovendo a desintoxicação do organismo.",
    benefits: ["Alívio de dores profundas", "Desintoxicação", "Estimulação circulatória", "Relaxamento muscular"],
    ideal: "Dores musculares profundas, tensão nas costas, fadiga",
  },
  {
    id: "aromaterapia",
    name: "Aromaterapia",
    icon: "🌿",
    duration: "50 min",
    description: "Uso terapêutico de óleos essenciais naturais para harmonizar estados emocionais, fortalecer o sistema imunológico e promover equilíbrio entre corpo e mente.",
    benefits: ["Equilíbrio emocional", "Fortalecimento imunológico", "Redução de ansiedade", "Harmonização energética"],
    ideal: "Ansiedade, desequilíbrio emocional, imunidade baixa",
  },
  {
    id: "reiki",
    name: "Reiki",
    icon: "🔮",
    duration: "60 min",
    description: "Técnica de canalização de energia vital que harmoniza os campos energéticos do corpo, dissolve bloqueios emocionais e promove cura profunda nos níveis físico, mental e espiritual.",
    benefits: ["Harmonização energética", "Cura emocional", "Paz interior", "Equilíbrio espiritual"],
    ideal: "Bloqueios emocionais, desequilíbrio energético, crise espiritual",
  },
];

export default function TherapiesGrid() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.remove("reveal-hidden");
            e.target.classList.add("reveal-active");
          }
        });
      },
      { threshold: 0.06 }
    );
    ref?.current?.querySelectorAll(".reveal-hidden")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section className="py-32 md:py-44 px-6 md:px-10 bg-cream" ref={ref}>
      <div className="max-w-8xl mx-auto">
        <div className="text-center mb-20">
          <span className="reveal-hidden subheading mb-5 block">6 Terapias Disponíveis</span>
          <h2 className="reveal-hidden reveal-delay-1 font-serif text-forest leading-tight"
            style={{ fontSize: 'clamp(2.2rem, 5vw, 3.5rem)' }}>
            Cuidado que vai{" "}
            <span className="italic font-light text-sage">além do corpo</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {therapies?.map((therapy, i) => (
            <div
              key={therapy?.id}
              className={`reveal-hidden reveal-delay-${(i % 3) + 1} bg-white rounded-4xl p-8 flex flex-col group transition-all duration-500 hover:-translate-y-2`}
              style={{
                boxShadow: '0 2px 16px rgba(26,61,26,0.06), 0 8px 32px rgba(26,61,26,0.04)',
                border: '1px solid rgba(122,158,126,0.2)',
              }}
            >
              {/* Icon circle + Duration */}
              <div className="flex items-center justify-between mb-8">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center text-2xl"
                  style={{ background: 'var(--gold-pale)', border: '1px solid rgba(201,168,76,0.3)' }}
                >
                  {therapy?.icon}
                </div>
                <span className="text-xs font-medium px-3 py-1.5 rounded-full" style={{ background: 'var(--cream-dark)', color: 'var(--stone)' }}>
                  {therapy?.duration}
                </span>
              </div>

              {/* Name */}
              <h3 className="font-serif mb-4 transition-colors" style={{ fontSize: '1.3rem', color: 'var(--forest)' }}>
                {therapy?.name}
              </h3>

              {/* Description */}
              <p className="text-sm leading-relaxed mb-6 flex-1" style={{ color: 'var(--stone)' }}>
                {therapy?.description}
              </p>

              {/* Benefits */}
              <div className="mb-6">
                <p className="text-xs uppercase tracking-widest mb-3 font-semibold" style={{ color: 'var(--stone-light)' }}>
                  Benefícios
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {therapy?.benefits?.map((b) => (
                    <span key={b} className="text-xs px-2.5 py-1 rounded-full" style={{ background: 'var(--sage-pale)', color: 'var(--forest-mid)' }}>
                      {b}
                    </span>
                  ))}
                </div>
              </div>

              {/* Ideal for */}
              <div className="pt-5" style={{ borderTop: '1px solid rgba(232,226,214,0.8)' }}>
                <p className="text-xs font-medium mb-1" style={{ color: 'var(--stone-light)' }}>Indicado para:</p>
                <p className="text-xs italic" style={{ color: 'var(--stone)' }}>{therapy?.ideal}</p>
              </div>

              {/* CTA */}
              <a
                href={`https://wa.me/5551999999999?text=Olá! Tenho interesse em ${therapy?.name}. Gostaria de agendar uma sessão.`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 flex items-center justify-center gap-2 text-white rounded-full py-3.5 text-sm font-semibold transition-all duration-300 hover:opacity-90 hover:shadow-lg"
                style={{ background: '#25D366' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Agendar sessão
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}